import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { RichTextDisplay } from '@/components/ui/rich-text-display';
import { Typography } from '@/components/ui/typography';
import { useConsentDocuments } from '@/hooks/useConsentDocuments';
import type { ConsentDocumentsProps } from '@/types/consent';

export const ConsentDocuments = ({
  saving,
  alreadyAccepted,
  onAccept,
  onBack,
  showChangeSummary = false,
}: ConsentDocumentsProps) => {
  const { t: tCommon } = useTranslation('common');
  const { documents, isLoading, isError, refetch } = useConsentDocuments();
  // Never restored from prior state: agreeing is an act the user performs, not a value we remember.
  const [accepted, setAccepted] = useState(false);

  const unavailable = isLoading || isError;
  const canContinue = alreadyAccepted || (accepted && !unavailable);

  return (
    // Flex column so the reader absorbs whatever height is left rather than claiming a fixed slice.
    <form
      onSubmit={event => {
        event.preventDefault();
        if (!canContinue || saving) return;
        onAccept();
      }}
      className='flex min-h-0 flex-col gap-4'
    >
      {isLoading && (
        <div
          role='status'
          aria-label={tCommon('loading.generic')}
          className='rounded-lg border border-gray-700 bg-slate-950/60 p-5'
        >
          <Skeleton className='h-6 w-1/2 bg-white/10' />
          <div className='mt-4 space-y-3'>
            <Skeleton className='h-4 w-full bg-white/10' />
            <Skeleton className='h-4 w-11/12 bg-white/10' />
            <Skeleton className='h-4 w-4/5 bg-white/10' />
          </div>
        </div>
      )}

      {isError && (
        <div className='flex flex-col items-center gap-4 rounded-lg border border-gray-700 bg-slate-950/60 py-10 text-center'>
          <FileText className='h-10 w-10 text-purple-400' />
          <Typography variant='p2' className='text-gray-300'>
            {tCommon('legal.notAvailable')}
          </Typography>
          <Button type='button' variant='outline' onClick={refetch}>
            {tCommon('buttons.tryAgain')}
          </Button>
        </div>
      )}

      {!isLoading &&
        !isError &&
        documents.map((document, index) => (
          <section
            key={document._id}
            className='flex min-h-0 flex-1 flex-col gap-3'
          >
            {/* Only the title stays pinned; it is what names the document while the text scrolls. */}
            <Typography variant='h5' as='h2' className='text-white'>
              {document.title}
            </Typography>

            {/* Instruction reads as a caption for the box, so it belongs against it, not at the top.
                First document only — it is a one-time instruction, not a per-document label. */}
            {index === 0 && (
              <Typography variant='p3' className='text-gray-400'>
                {tCommon(
                  alreadyAccepted ? 'consent.alreadyAccepted' : 'consent.intro',
                )}
              </Typography>
            )}

            {/* `flex-1` lets the reader shrink to fit the dialog; the max-height caps it in the
                wizard, where no flex parent bounds it. Divided so N documents share one budget. */}
            <div
              className='min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-700 bg-slate-950/60 p-5'
              style={{ maxHeight: `calc(60vh / ${documents.length})` }}
            >
              {/* Scrolls with the body so a long note never eats the reader's height. Per document,
                  so a future second one carries its own. Literal purple, not `var(--brand-primary)`:
                  an opacity modifier cannot apply to an arbitrary var() and renders flat. */}
              {showChangeSummary && document.summaryOfChanges?.trim() && (
                <div className='mb-4 rounded-lg border border-purple-500/30 bg-purple-500/10 p-3'>
                  <Typography
                    variant='p3'
                    as='h3'
                    className='mb-1 font-semibold uppercase tracking-wide text-purple-200'
                  >
                    {tCommon('consent.whatChanged')}
                  </Typography>
                  <Typography variant='p3' className='text-gray-200'>
                    {document.summaryOfChanges}
                  </Typography>
                </div>
              )}

              <RichTextDisplay
                content={document.bodyHtml ?? ''}
                className='legal-document'
                forceLeftAlign
              />
            </div>
          </section>
        ))}

      <label className='flex items-start gap-3 cursor-pointer'>
        <Checkbox
          checked={alreadyAccepted || accepted}
          onCheckedChange={value => setAccepted(value === true)}
          // Ticked-and-disabled is a record of what they already did, not a pre-ticked box asking for consent.
          disabled={alreadyAccepted || unavailable}
          className='mt-0.5 h-4 w-4 rounded border border-gray-500 bg-gray-900/80 data-[state=checked]:bg-[var(--brand-primary)] data-[state=checked]:border-[var(--brand-primary)] data-[state=checked]:text-white'
        />
        <Typography variant='p3' as='span' className='text-gray-300'>
          {tCommon('consent.checkbox')}
        </Typography>
      </label>

      <div className='flex gap-2 w-full'>
        {onBack && (
          <Button
            type='button'
            variant='outline'
            className='flex-1 border-gray-600 text-gray-300 hover:bg-gray-800'
            onClick={onBack}
          >
            <ChevronLeft className='rtl:rotate-180 w-4 h-4 me-1' />
            {tCommon('completeProfile.back')}
          </Button>
        )}
        <Button
          type='submit'
          className='flex-1 bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)] hover:opacity-90 hover:text-white transition-colors'
          disabled={!canContinue || saving}
        >
          {saving ? (
            <Loader2 className='w-4 h-4 animate-spin' />
          ) : (
            tCommon('completeProfile.continue')
          )}
        </Button>
      </div>
    </form>
  );
};
